/**
 * Shared PII helpers for third-party logging / analytics (Rollbar, LogRocket,
 * Gainsight PX). Call the named helper you need — no provider switch.
 *
 * To extend coverage:
 *   - protect a new field/param/payload key → add it to `PII_FIELDS`
 *   - protect a new secret header           → add it to `SENSITIVE_HEADERS`
 */

/** Field / query-param / payload key names that may carry PII and must be scrubbed. */
export const PII_FIELDS = [
  'loginHint',
  'userEmail',
  'email',
  'employeeId',
  'phoneNumber',
  // Auth secrets that appear in our URLs
  'reset',
  'code',
  'session',
  'teamsJwt',
] as const

/** Header names that carry auth secrets; never transmit them to a third party. */
export const SENSITIVE_HEADERS = ['Authorization'] as const

/** Placeholder written in place of a redacted PII query-param value. */
export const REDACTED_VALUE = 'REDACTED'

export type PiiField = (typeof PII_FIELDS)[number]
export type SensitiveHeader = (typeof SENSITIVE_HEADERS)[number]

const equalsIgnoreCase = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase()

/** Case-insensitive check for whether a key/param name is considered PII. */
export const isPiiField = (name: string): boolean =>
  PII_FIELDS.some(field => equalsIgnoreCase(field, name))

/** Case-insensitive check for whether a header name carries an auth secret. */
export const isSensitiveHeader = (name: string): boolean =>
  SENSITIVE_HEADERS.some(header => equalsIgnoreCase(header, name))

/**
 * Removes any PII-named keys from an arbitrary record before it is handed to an
 * analytics provider (e.g. Gainsight identify traits). Matching is case-insensitive.
 */
export const scrubTraits = <T extends Record<string, unknown>>(record: T): Partial<T> =>
  Object.fromEntries(Object.entries(record).filter(([key]) => !isPiiField(key))) as Partial<T>

/**
 * Blank sensitive auth headers in place before network metadata is sent to a
 * third party (e.g. LogRocket request sanitizer).
 */
export const sanitizeHeaders = (headers: Record<string, string | null | undefined>): void => {
  Object.keys(headers).forEach(name => {
    const value = headers[name]
    if (isSensitiveHeader(name) && value !== null && value !== undefined) {
      headers[name] = ''
    }
  })
}

/**
 * Sanitizes a query-param value that may itself be a URL.
 *
 * `URLSearchParams` decodes each value exactly once, which is enough for a
 * well-formed redirect chain but not for a value encoded more times than its
 * slot requires. There the nested `?` arrives as `%3F` and the query is
 * invisible, so peel encoding layers until it surfaces, then re-encode the
 * sanitized result to the depth it was found at.
 *
 * A value that does not decode (or decodes to no query string) is returned as
 * given — we do not special-case malformed escapes beyond that.
 */
const sanitizeNestedValue = (value: string): string => {
  if (value.includes('?')) return sanitizeUrl(value)

  let decoded: string
  try {
    decoded = decodeURIComponent(value)
  } catch {
    return value
  }

  // Decoding never lengthens a string, so peeling always terminates.
  if (decoded === value) return value

  const sanitized = sanitizeNestedValue(decoded)
  return sanitized === decoded ? value : encodeURIComponent(sanitized)
}

/**
 * Redacts PII-bearing query parameters from a URL before it is sent to a
 * third-party (LogRocket page/network URLs). Any query param whose name matches
 * `PII_FIELDS` has its value replaced with `REDACTED_VALUE`; everything else
 * (path, other params, hash) is preserved.
 *
 * Also recurses into any query-param *value* that itself looks like a URL with
 * a query string (e.g. `?redirectTo=/home?userEmail=ada%40kudos.com`) so nested
 * PII is redacted regardless of the outer param name, and regardless of how many
 * times that value was percent-encoded (see {@link sanitizeNestedValue}).
 *
 * Matching is case-insensitive. Accepts absolute, protocol-relative, root-relative
 * and relative URLs. Everything before the query string (scheme, host, path) and
 * the fragment are preserved verbatim from the input — only the query string is
 * rewritten — so the return value keeps the form it was given. The exception is a
 * URL whose query is percent-encoded: it has to be decoded to be inspected at all,
 * so a redacted result comes back re-encoded as a whole rather than byte-for-byte.
 *
 * Returns the input unchanged when there are no PII params. If parsing fails,
 * returns `REDACTED_VALUE` (fail closed) so a malformed URL cannot leak PII.
 */
export const sanitizeUrl = (url: string): string => {
  if (!url) return url

  // A URL whose query is itself percent-encoded (`/login%3FuserEmail%3D…`) has
  // no `?` for `URL` to split on, so `searchParams` comes back empty and every
  // param below is invisible. Peel the encoding before parsing rather than
  // handing back a string we never actually inspected. If peeling surfaces
  // nothing, fall through so a malformed URL still fail-closes below.
  if (!url.includes('?')) {
    const peeled = sanitizeNestedValue(url)
    if (peeled !== url) return peeled
  }

  try {
    // Relative URLs (e.g. `/login?userEmail=…`) throw without a base; the origin
    // is only used so `URL` can parse path/query. Absolute inputs ignore it, and
    // for relative inputs we drop it again when rebuilding the result below.
    const parsed = new URL(url, window.location.origin)
    const parsedEntries = [...parsed.searchParams.entries()]
    let hasRedactedAValue = false

    parsedEntries.forEach(([key, value]) => {
      if (isPiiField(key)) {
        if (value !== REDACTED_VALUE) {
          parsed.searchParams.set(key, REDACTED_VALUE)
          hasRedactedAValue = true
        }
        return
      }

      // Any param whose value is itself a URL with a query string can carry
      // nested PII (redirect targets, return URLs, etc.), however deeply the
      // value happens to be encoded.
      const sanitizedValue = sanitizeNestedValue(value)
      if (sanitizedValue !== value) {
        parsed.searchParams.set(key, sanitizedValue)
        hasRedactedAValue = true
      }
    })

    if (!hasRedactedAValue) return url

    // Splice the rewritten query back into the original string instead of
    // rebuilding from `parsed`. `URL` resolves relative inputs against the base,
    // so rebuilding would rewrite query-only (`?a=b`), path-relative (`a/b?c=d`)
    // and protocol-relative (`//host/p?q=1`) inputs into root-relative paths —
    // the last of which would silently drop the host.
    const queryStart = url.indexOf('?')
    if (queryStart === -1) return url

    const prefix = url.slice(0, queryStart)
    const hashStart = url.indexOf('#', queryStart)
    const hash = hashStart === -1 ? '' : url.slice(hashStart)

    return `${prefix}?${parsed.searchParams.toString()}${hash}`
  } catch {
    // Fail closed: we could not inspect the query string, so do not risk
    // forwarding an opaque value that may contain PII.
    return REDACTED_VALUE
  }
}
